import React, { useState } from "react";
import { XCircle, X, AlertCircle, Loader2, FileText } from "lucide-react";

/**
 * Cancel Ticket Modal Component
 * Allows cancellation of an attendee's ticket with a reason
 */
const CancelTicketModal = ({
  isOpen,
  onClose,
  onConfirm,
  attendee,
  isLoading = false,
}) => {
  const [reason, setReason] = useState("");
  const [errors, setErrors] = useState({});

  if (!isOpen || !attendee) return null;

  const validate = () => {
    const newErrors = {};
    if (!reason.trim()) {
      newErrors.reason = "Cancellation reason is required";
    } else if (reason.trim().length < 10) {
      newErrors.reason =
        "Please provide a more detailed reason (at least 10 characters)";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onConfirm({ reason: reason.trim() });
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setReason("");
      setErrors({});
      onClose();
    }
  };

  return (
    <div className="flex fixed inset-0 z-50 justify-center items-center p-4">
      <div
        className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity"
        onClick={handleClose}
      />
      <div className="relative p-6 w-full max-w-md bg-white rounded-2xl shadow-2xl transform transition-all">
        {/* Header */}
        <div className="flex items-start mb-6 space-x-4">
          <div className="flex-shrink-0 p-3 bg-red-100 rounded-full">
            <XCircle className="w-6 h-6 text-red-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="mb-1 text-xl font-bold text-gray-900">
              Cancel Ticket
            </h3>
            <p className="text-sm text-gray-600">
              Cancel ticket for{" "}
              <span className="font-semibold text-gray-900">
                {attendee.name || "this attendee"}
              </span>
              ?
            </p>
          </div>
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="flex-shrink-0 p-1 text-gray-400 rounded-lg transition-colors hover:text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Attendee Info */}
        <div className="p-4 mb-6 bg-gray-50 rounded-xl border border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="flex overflow-hidden flex-shrink-0 justify-center items-center w-12 h-12 bg-gradient-to-br from-purple-400 to-purple-600 rounded-lg">
              <span className="text-sm font-semibold text-white">
                {(attendee.name || "U")
                  .split(" ")
                  .map((n) => n[0])
                  .join("")
                  .substring(0, 2)
                  .toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">
                {attendee.name || "Attendee"}
              </p>
              <p className="text-xs text-gray-600">
                {attendee.ticket_type || "N/A"} • $
                {parseFloat(attendee.amount_paid || 0).toFixed(2)}
              </p>
            </div>
          </div>
        </div>

        {/* Warning Message */}
        <div className="p-4 mb-6 bg-red-50 rounded-xl border border-red-100">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-red-800">
              <p className="mb-1 font-medium">Important:</p>
              <ul className="space-y-1 list-disc list-inside">
                <li>This action cannot be undone</li>
                <li>A refund will be processed according to your refund policy</li>
                <li>The attendee will be notified automatically</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="mb-6">
            <label className="flex items-center mb-2 text-sm font-semibold text-gray-700">
              <FileText className="mr-2 w-4 h-4 text-red-600" />
              Cancellation Reason
              <span className="ml-1 text-red-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                setErrors((prev) => ({ ...prev, reason: "" }));
              }}
              rows={4}
              placeholder="Please provide a detailed reason for cancelling this ticket..."
              className={`w-full px-4 py-3 text-sm border rounded-xl transition-all resize-none focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                errors.reason
                  ? "border-red-300 bg-red-50"
                  : "border-gray-300 bg-white"
              }`}
              disabled={isLoading}
            />
            <div className="flex items-center justify-between mt-1.5">
              {errors.reason ? (
                <p className="flex items-center text-xs text-red-600">
                  <AlertCircle className="mr-1 w-3 h-3" />
                  {errors.reason}
                </p>
              ) : (
                <p className="text-xs text-gray-500">
                  Minimum 10 characters required
                </p>
              )}
              <p className="text-xs text-gray-500">
                {reason.length}/500 characters
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={isLoading}
              className="flex-1 px-4 py-3 font-semibold text-gray-700 bg-gray-100 rounded-xl transition-colors hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Keep Ticket
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex flex-1 justify-center items-center px-4 py-3 font-semibold text-white bg-red-600 rounded-xl transition-colors hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                  Cancelling...
                </>
              ) : (
                "Cancel Ticket"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CancelTicketModal;

